/**
 *   This is a service for a specific store module which uses the api.ts Axios instance as a base.
 *   We separate our files for easy managment and readibility.
 *   We export this as a service to be used in other areas of the application.
 */

import Api from '@/services/api';
import AppService from '@/services/app';
import Store from '@/store/index';

export default {

  resetIndustries() {
    // reset store to empty
    Store.dispatch('customers/setIndustries', {
      industries: [],
    });
  },

  async listAllIndustries(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['customers/industries'].length < 1) {
      try {
        const response = await Api(true).get('/app/list_industries');

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.resetIndustries();
            return response.data.message;
          } else {
            Store.dispatch('customers/setIndustries', {
              industries: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        this.resetIndustries();
        if (error.response) {
          return error.response.statusText;
        } else {
          AppService.logSupportDebug('customer.ts - listAllIndustries - 89 - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      } 
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  resetIndustrySectors() {
    // reset store to empty
    Store.dispatch('customers/setIndustrySectors', {
      industrySectors: [],
    });
  },

  async listAllIndustrySectors(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['customers/industrySectors'].length < 1) {
      try {
        const response = await Api(true).get('/app/list_industry_sectors');

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.resetIndustrySectors();
            return response.data.message;
          } else {
            Store.dispatch('customers/setIndustrySectors', {
              industrySectors: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        this.resetIndustries();
        if (error.response) {
          return error.response.statusText;
        } else {
          AppService.logSupportDebug('customer.ts - listAllIndustriesSectors - 89 - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      } 
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },
  
  completeRegistration( formData: FormData ) {
    // NOTE: We need to now explicitly set the headers to multipart/form-data. In earlier versions of axios ("axios": "^0.19.0") this was done implicitly, but this no longer seems to work on the newer version ("axios": "^1.2.1")
    return Api(true).post('/customer/register', formData, {
        headers: {
          'Accept': 'multipart/form-data',
          'Content-Type': 'multipart/form-data',
        },
      });
  },

  updatePackage(params: { 
    paymentFrequencyId: number,
    packageTypeId: number,
  }) {
    return Api(true).post('/customer/update_package', params);
  },

  get() {
    return Api(true).get('/customer/get');
  },

  getCustomerAccommodation() {
    return Api(true).get('/customer/accommodation');
  },

  getCustomerRestaurant() {
    return Api(true).get('/customer/restaurant');
  },

  getCustomerSupplier() {
    return Api(true).get('/customer/supplier');
  },

  update(params: {
    tradingName?: string,
    regNumber: string,
    vatNumber?: string,
    addressLine1: string,
    addressLine2?: string,
    addressLine3?: string,
    city: string,
    province: string,
    country: string,
    postalCode: string,
    publicPhoneNumber: string,
    publicEmail: string,
    publicWhatsapp?: string,
    website?: string,
    mainContactName: string,
    mainContactPosition: string,
    mainContactPhone: string,
    mainContactEmail: string,
    industrySectorIds: string,
    rooms?: number,
    covers?: number,
    operatingHours?: string,
    distributionTypeId?: number,
    seasonalClosure?: string,
  }) {
    return Api(true).post('/customer/update', params);
  },

  listDocuments() {
    return Api(true).get('/customer/list_documents');
  },

};
